<div class="construction-document-checklist">
  <!-- Header with Stats -->
  <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
    <div class="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
      <div>
        <h2 class="text-xl font-bold text-gray-900 flex items-center gap-2">
          <mat-icon class="text-primary">fact_check</mat-icon>
          Construction Documents & Approvals
        </h2>
        <p class="text-gray-600 text-sm mt-1">
          Track all required documents and approvals for your {{project?.domain || domain}} - {{project?.sub_domain || subDomain}} project
        </p>
      </div>
      <button 
        (click)="exportChecklist()"
        class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2">
        <mat-icon class="text-sm">download</mat-icon>
        Export Checklist
      </button>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
      <div class="bg-gray-50 rounded-lg p-4 text-center">
        <p class="text-2xl font-bold text-gray-900">{{stats.total}}</p>
        <p class="text-xs text-gray-600">Total Documents</p>
      </div>
      <div class="bg-green-50 rounded-lg p-4 text-center">
        <p class="text-2xl font-bold text-green-600">{{stats.approved}}</p>
        <p class="text-xs text-gray-600">Approved</p>
      </div>
      <div class="bg-yellow-50 rounded-lg p-4 text-center">
        <p class="text-2xl font-bold text-yellow-600">{{stats.pending}}</p>
        <p class="text-xs text-gray-600">Pending</p>
      </div>
      <div class="bg-blue-50 rounded-lg p-4 text-center">
        <p class="text-2xl font-bold text-blue-600">{{stats.in_progress}}</p>
        <p class="text-xs text-gray-600">In Progress</p>
      </div>
      <div class="bg-red-50 rounded-lg p-4 text-center">
        <p class="text-2xl font-bold text-red-600">{{stats.rejected}}</p>
        <p class="text-xs text-gray-600">Rejected</p>
      </div>
      <div class="bg-primary/10 rounded-lg p-4 text-center">
        <p class="text-2xl font-bold text-primary">{{stats.completion_percentage}}%</p>
        <p class="text-xs text-gray-600">Complete</p>
      </div>
    </div>

    <!-- Progress Bar -->
    <div class="mt-4">
      <div class="flex items-center justify-between mb-1">
        <span class="text-sm text-gray-600">Overall Progress</span>
        <span class="text-sm font-medium text-gray-900">{{stats.approved}} / {{stats.total}}</span>
      </div>
      <div class="w-full bg-gray-200 rounded-full h-2">
        <div 
          class="bg-green-500 h-2 rounded-full transition-all duration-500"
          [style.width.%]="stats.completion_percentage">
        </div>
      </div>
    </div>
  </div>

  <!-- Filters -->
  <div class="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
    <div class="flex flex-col md:flex-row gap-4">
      <div class="flex-1">
        <input 
          type="text"
          [(ngModel)]="searchKeyword"
          placeholder="Search documents..."
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
      </div>
      <div class="w-48">
        <select 
          [(ngModel)]="filterStatus"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
          <option value="">All Status</option>
          <option *ngFor="let status of statusOptions" [value]="status.value">{{status.label}}</option>
        </select>
      </div>
      <div class="w-48">
        <select 
          [(ngModel)]="filterMandatory"
          class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
          <option value="">All Documents</option>
          <option value="true">Mandatory Only</option>
          <option value="false">Optional Only</option>
        </select>
      </div>
      <button 
        (click)="clearFilters()"
        class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
        Clear
      </button>
    </div>
  </div>

  <!-- Loading State -->
  <div *ngIf="isLoading" class="flex justify-center items-center py-12">
    <mat-spinner diameter="40"></mat-spinner>
  </div>

  <!-- Document Stages -->
  <div *ngIf="!isLoading" class="space-y-4">
    <!-- Feasibility Stage -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div 
        (click)="toggleStage('feasibility')"
        class="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <mat-icon class="text-blue-600">{{getStageIcon('feasibility')}}</mat-icon>
          </div>
          <div>
            <h3 class="font-semibold text-gray-900">1. Feasibility Stage</h3>
            <p class="text-sm text-gray-600">Pre-construction ownership and clearances</p>
          </div>
        </div>
        <div class="flex items-center gap-4">
          <span class="text-sm text-gray-600">
            {{getStageApprovedCount('feasibility')}} / {{getStageCount('feasibility')}} complete
          </span>
          <mat-icon class="text-gray-400 transition-transform" [class.rotate-180]="expandedStages['feasibility']">
            expand_more
          </mat-icon>
        </div>
      </div>
      
      <div *ngIf="expandedStages['feasibility']" class="p-4">
        <div class="space-y-3">
          <div 
            *ngFor="let doc of getFilteredDocuments('feasibility')"
            class="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <div class="flex-shrink-0 pt-1">
              <mat-checkbox 
                [checked]="doc.status === 'approved'"
                (change)="quickStatusUpdate(doc, $event.checked ? 'approved' : 'pending')"
                color="primary">
              </mat-checkbox>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between gap-2">
                <div>
                  <h4 class="font-medium text-gray-900 flex items-center gap-2">
                    {{doc.document_name}}
                    <span *ngIf="doc.is_mandatory" class="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">Mandatory</span>
                  </h4>
                  <p class="text-sm text-gray-600 mt-1">{{doc.description}}</p>
                  <p class="text-xs text-gray-500 mt-1">
                    <strong>Authority:</strong> {{doc.issuing_authority}}
                  </p>
                </div>
                <span class="px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0" [ngClass]="getStatusClass(doc.status || 'pending')">
                  {{getStatusLabel(doc.status || 'pending')}}
                </span>
              </div>
              
              <div *ngIf="doc.reference_number || doc.approval_date" class="mt-2 flex flex-wrap gap-4 text-xs text-gray-500">
                <span *ngIf="doc.reference_number"><strong>Ref:</strong> {{doc.reference_number}}</span>
                <span *ngIf="doc.approval_date"><strong>Approved:</strong> {{doc.approval_date | date:'mediumDate'}}</span>
                <span *ngIf="doc.expiry_date"><strong>Expiry:</strong> {{doc.expiry_date | date:'mediumDate'}}</span>
              </div>
              
              <div class="mt-3 flex items-center gap-2 flex-wrap">
                <button 
                  (click)="openUpdateDialog(doc)"
                  class="text-xs text-primary hover:underline flex items-center gap-1">
                  <mat-icon class="text-sm">edit</mat-icon>
                  Update Details
                </button>
                <a
                  *ngIf="getFileUrl(doc) as fileUrl"
                  [href]="fileUrl"
                  target="_blank"
                  rel="noopener"
                  class="text-xs text-green-700 hover:underline flex items-center gap-1">
                  <mat-icon class="text-sm">attach_file</mat-icon>
                  {{ doc.file_name || 'View uploaded file' }}
                </a>
              </div>
            </div>
          </div>

          <div *ngIf="getFilteredDocuments('feasibility').length === 0" class="text-center py-8 text-gray-500">
            No documents found for this stage
          </div>
        </div>
      </div>
    </div>

    <!-- Sanction Stage -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div 
        (click)="toggleStage('sanction')"
        class="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
            <mat-icon class="text-yellow-600">{{getStageIcon('sanction')}}</mat-icon>
          </div>
          <div>
            <h3 class="font-semibold text-gray-900">2. Sanction Stage</h3>
            <p class="text-sm text-gray-600">Construction permits, NOCs, and approvals</p>
          </div>
        </div>
        <div class="flex items-center gap-4">
          <span class="text-sm text-gray-600">
            {{getStageApprovedCount('sanction')}} / {{getStageCount('sanction')}} complete
          </span>
          <mat-icon class="text-gray-400 transition-transform" [class.rotate-180]="expandedStages['sanction']">
            expand_more
          </mat-icon>
        </div>
      </div>
      
      <div *ngIf="expandedStages['sanction']" class="p-4">
        <div class="space-y-3">
          <div 
            *ngFor="let doc of getFilteredDocuments('sanction')"
            class="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <div class="flex-shrink-0 pt-1">
              <mat-checkbox 
                [checked]="doc.status === 'approved'"
                (change)="quickStatusUpdate(doc, $event.checked ? 'approved' : 'pending')"
                color="primary">
              </mat-checkbox>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between gap-2">
                <div>
                  <h4 class="font-medium text-gray-900 flex items-center gap-2">
                    {{doc.document_name}}
                    <span *ngIf="doc.is_mandatory" class="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">Mandatory</span>
                  </h4>
                  <p class="text-sm text-gray-600 mt-1">{{doc.description}}</p>
                  <p class="text-xs text-gray-500 mt-1">
                    <strong>Authority:</strong> {{doc.issuing_authority}}
                  </p>
                </div>
                <span class="px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0" [ngClass]="getStatusClass(doc.status || 'pending')">
                  {{getStatusLabel(doc.status || 'pending')}}
                </span>
              </div>
              
              <div *ngIf="doc.reference_number || doc.approval_date" class="mt-2 flex flex-wrap gap-4 text-xs text-gray-500">
                <span *ngIf="doc.reference_number"><strong>Ref:</strong> {{doc.reference_number}}</span>
                <span *ngIf="doc.approval_date"><strong>Approved:</strong> {{doc.approval_date | date:'mediumDate'}}</span>
                <span *ngIf="doc.expiry_date"><strong>Expiry:</strong> {{doc.expiry_date | date:'mediumDate'}}</span>
              </div>
              
              <div class="mt-3 flex items-center gap-2 flex-wrap">
                <button 
                  (click)="openUpdateDialog(doc)"
                  class="text-xs text-primary hover:underline flex items-center gap-1">
                  <mat-icon class="text-sm">edit</mat-icon>
                  Update Details
                </button>
                <a
                  *ngIf="getFileUrl(doc) as fileUrl"
                  [href]="fileUrl"
                  target="_blank"
                  rel="noopener"
                  class="text-xs text-green-700 hover:underline flex items-center gap-1">
                  <mat-icon class="text-sm">attach_file</mat-icon>
                  {{ doc.file_name || 'View uploaded file' }}
                </a>
              </div>
            </div>
          </div>

          <div *ngIf="getFilteredDocuments('sanction').length === 0" class="text-center py-8 text-gray-500">
            No documents found for this stage
          </div>
        </div>
      </div>
    </div>

    <!-- Completion Stage -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <div 
        (click)="toggleStage('completion')"
        class="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors border-b border-gray-100">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <mat-icon class="text-green-600">{{getStageIcon('completion')}}</mat-icon>
          </div>
          <div>
            <h3 class="font-semibold text-gray-900">3. After Completion</h3>
            <p class="text-sm text-gray-600">Final certificates and connections</p>
          </div>
        </div>
        <div class="flex items-center gap-4">
          <span class="text-sm text-gray-600">
            {{getStageApprovedCount('completion')}} / {{getStageCount('completion')}} complete
          </span>
          <mat-icon class="text-gray-400 transition-transform" [class.rotate-180]="expandedStages['completion']">
            expand_more
          </mat-icon>
        </div>
      </div>
      
      <div *ngIf="expandedStages['completion']" class="p-4">
        <div class="space-y-3">
          <div 
            *ngFor="let doc of getFilteredDocuments('completion')"
            class="flex items-start gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
            <div class="flex-shrink-0 pt-1">
              <mat-checkbox 
                [checked]="doc.status === 'approved'"
                (change)="quickStatusUpdate(doc, $event.checked ? 'approved' : 'pending')"
                color="primary">
              </mat-checkbox>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between gap-2">
                <div>
                  <h4 class="font-medium text-gray-900 flex items-center gap-2">
                    {{doc.document_name}}
                    <span *ngIf="doc.is_mandatory" class="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">Mandatory</span>
                  </h4>
                  <p class="text-sm text-gray-600 mt-1">{{doc.description}}</p>
                  <p class="text-xs text-gray-500 mt-1">
                    <strong>Authority:</strong> {{doc.issuing_authority}}
                  </p>
                </div>
                <span class="px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0" [ngClass]="getStatusClass(doc.status || 'pending')">
                  {{getStatusLabel(doc.status || 'pending')}}
                </span>
              </div>
              
              <div *ngIf="doc.reference_number || doc.approval_date" class="mt-2 flex flex-wrap gap-4 text-xs text-gray-500">
                <span *ngIf="doc.reference_number"><strong>Ref:</strong> {{doc.reference_number}}</span>
                <span *ngIf="doc.approval_date"><strong>Approved:</strong> {{doc.approval_date | date:'mediumDate'}}</span>
                <span *ngIf="doc.expiry_date"><strong>Expiry:</strong> {{doc.expiry_date | date:'mediumDate'}}</span>
              </div>
              
              <div class="mt-3 flex items-center gap-2 flex-wrap">
                <button 
                  (click)="openUpdateDialog(doc)"
                  class="text-xs text-primary hover:underline flex items-center gap-1">
                  <mat-icon class="text-sm">edit</mat-icon>
                  Update Details
                </button>
                <a
                  *ngIf="getFileUrl(doc) as fileUrl"
                  [href]="fileUrl"
                  target="_blank"
                  rel="noopener"
                  class="text-xs text-green-700 hover:underline flex items-center gap-1">
                  <mat-icon class="text-sm">attach_file</mat-icon>
                  {{ doc.file_name || 'View uploaded file' }}
                </a>
              </div>
            </div>
          </div>

          <div *ngIf="getFilteredDocuments('completion').length === 0" class="text-center py-8 text-gray-500">
            No documents found for this stage
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Update Dialog -->
  <div *ngIf="showUpdateDialog" class="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    <div class="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
      <div class="p-6 border-b border-gray-200">
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-semibold text-gray-900">Update Document Status</h3>
          <button (click)="closeUpdateDialog()" class="text-gray-400 hover:text-gray-600">
            <mat-icon>close</mat-icon>
          </button>
        </div>
        <p class="text-sm text-gray-600 mt-1">{{selectedDocument?.document_name}}</p>
      </div>
      
      <form [formGroup]="updateForm" (ngSubmit)="saveDocumentUpdate()" class="p-6 space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select formControlName="status" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
              <option *ngFor="let status of statusOptions" [value]="status.value">{{status.label}}</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <select formControlName="priority" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
              <option *ngFor="let p of priorityOptions" [value]="p.value">{{p.label}}</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Reference Number</label>
            <input type="text" formControlName="reference_number" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none" placeholder="Enter reference number">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Assigned To</label>
            <select formControlName="assigned_to" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
              <option [value]="null">Not Assigned</option>
              <option *ngFor="let emp of employees" [value]="emp.id">{{emp.full_name}}</option>
            </select>
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Applied Date</label>
            <input type="date" formControlName="applied_date" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Submission Date</label>
            <input type="date" formControlName="submission_date" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Approval Date</label>
            <input type="date" formControlName="approval_date" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
          </div>
          
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
            <input type="date" formControlName="expiry_date" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none">
          </div>
        </div>
        
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
          <textarea formControlName="remarks" rows="3" class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none resize-none" placeholder="Enter any remarks..."></textarea>
        </div>
        
        <div class="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button type="button" (click)="closeUpdateDialog()" class="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button type="submit" class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors">
            Save Changes
          </button>
        </div>
      </form>
    </div>
  </div>
</div>
